/**
 * Atlas — Projects list page.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI } from '../services/api';

const STATUS_COLORS = {
  active:    { bg: '#dcfce7', color: '#15803d' },
  on_hold:   { bg: '#fef9c3', color: '#a16207' },
  completed: { bg: '#e0e7ff', color: '#4338ca' },
  archived:  { bg: '#f3f4f6', color: '#6b7280' },
};

const PROJECT_TYPES = [
  '', 'Residential', 'Commercial', 'Mixed Use', 'Subdivision',
  'Renovation', 'Townhouse', 'Apartment', 'Industrial', 'Retail',
];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await projectAPI.list();
      setProjects(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-wrap">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 28 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            Development Projects
          </div>
          <h1 className="serif" style={{ fontSize: 32, margin: '4px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
            Projects
          </h1>
        </div>
        <button className="btn primary" onClick={() => setShowCreate(true)}>
          + New Project
        </button>
      </div>

      {/* Project grid */}
      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', height: 200, color: 'var(--ink-4)' }}>Loading…</div>
      ) : projects.length === 0 ? (
        <EmptyState onNew={() => setShowCreate(true)} />
      ) : (
        <div className="grid-cards">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => { setShowCreate(false); navigate(`/projects/${p.id}`); }}
        />
      )}
    </div>
  );
}

function ProjectCard({ project, onClick }) {
  const s = STATUS_COLORS[project.status] || STATUS_COLORS.active;
  const address = [project.address_line1, project.suburb, project.state].filter(Boolean).join(', ');
  const budget = project.budget
    ? `$${project.budget.toLocaleString('en-AU')}`
    : null;

  const pct = project.task_count > 0
    ? Math.round((project.task_count * 0.3))   // placeholder progress
    : 0;

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12,
        padding: 22, cursor: 'pointer', transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = ''}
    >
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="serif" style={{ fontSize: 17, fontWeight: 600, color: 'var(--forest)', marginBottom: 2 }}>
            {project.name}
          </div>
          {address && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>📍 {address}</div>
          )}
        </div>
        <span style={{
          padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
          background: s.bg, color: s.color, marginLeft: 10, flexShrink: 0,
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {project.status.replace('_', ' ')}
        </span>
      </div>

      {/* Type + budget row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 12, color: 'var(--ink-3)' }}>
        {project.project_type && <span>{project.project_type}</span>}
        {budget && <span style={{ color: 'var(--forest)', fontWeight: 600 }}>{budget}</span>}
        {project.target_completion && (
          <span>Due {new Date(project.target_completion).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}</span>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--ink-4)', paddingTop: 12, borderTop: '1px solid var(--hairline)' }}>
        <span><strong style={{ color: 'var(--ink)' }}>{project.stage_count}</strong> stages</span>
        <span><strong style={{ color: 'var(--ink)' }}>{project.task_count}</strong> tasks</span>
        <span><strong style={{ color: 'var(--ink)' }}>{project.member_count}</strong> team</span>
        <span style={{ marginLeft: 'auto', color: 'var(--ink-4)' }}>
          {new Date(project.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 40px' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🏗</div>
      <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: 'var(--forest)', marginBottom: 8 }}>
        No projects yet
      </div>
      <p style={{ fontSize: 14, color: 'var(--ink-4)', maxWidth: 400, margin: '0 auto 24px' }}>
        Create your first development project to start managing stages, tasks, team members, and progress.
      </p>
      <button className="btn primary lg" onClick={onNew}>+ Create your first project</button>
    </div>
  );
}

// ── Address state abbreviations ───────────────────────────────────────────────
const STATE_ABBR = {
  'New South Wales': 'NSW', 'Victoria': 'VIC', 'Queensland': 'QLD',
  'South Australia': 'SA', 'Western Australia': 'WA', 'Tasmania': 'TAS',
  'Northern Territory': 'NT', 'Australian Capital Territory': 'ACT',
};

// ── Address autocomplete using Nominatim (OpenStreetMap) ─────────────────────
function AddressAutocomplete({ value, onChange, onSelect, fieldStyle }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    clearTimeout(debounceRef.current);
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&countrycodes=au&limit=6`,
          { headers: { 'Accept-Language': 'en-AU', 'User-Agent': 'PropAtlas/1.0' } }
        );
        const data = await res.json();
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 320);
  };

  const handleSelect = (item) => {
    const a = item.address;
    const streetParts = [a.house_number, a.road].filter(Boolean);
    const street = streetParts.join(' ') || a.pedestrian || a.path || '';
    const suburb = a.suburb || a.town || a.village || a.city_district || a.city || '';
    const stateFull = a.state || '';
    const state = STATE_ABBR[stateFull] || stateFull;
    const postcode = a.postcode || '';
    onSelect({ address_line1: street, suburb, state, postcode });
    onChange(street);
    setSuggestions([]);
    setOpen(false);
  };

  // Build display label for each suggestion
  const label = (item) => {
    const a = item.address;
    const parts = [
      [a.house_number, a.road].filter(Boolean).join(' '),
      a.suburb || a.town || a.village || a.city_district || '',
      STATE_ABBR[a.state] || a.state || '',
      a.postcode || '',
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...fieldStyle, paddingRight: 32 }}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Start typing a street address…"
          autoComplete="off"
        />
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 13, opacity: 0.4, pointerEvents: 'none',
        }}>🔍</span>
      </div>
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--card)', border: '1px solid var(--hairline)',
          borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
          marginTop: 3, overflow: 'hidden',
        }}>
          {suggestions.map((item) => (
            <button
              key={item.place_id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '9px 13px', background: 'none', border: 'none',
                borderBottom: '1px solid var(--hairline)', cursor: 'pointer',
                fontSize: 12, color: 'var(--ink)', lineHeight: 1.4,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--paper-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              <span style={{ marginRight: 6, opacity: 0.5 }}>📍</span>
              {label(item)}
            </button>
          ))}
          <div style={{ padding: '5px 10px', fontSize: 10, color: 'var(--ink-4)', textAlign: 'right', borderTop: '1px solid var(--hairline)' }}>
            © OpenStreetMap contributors
          </div>
        </div>
      )}
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    project_type: '',
    address_line1: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'AU',
    budget: '',
    start_date: '',
    target_completion: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Project name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        budget: form.budget ? parseFloat(form.budget) : undefined,
        start_date: form.start_date || undefined,
        target_completion: form.target_completion || undefined,
        project_type: form.project_type || undefined,
        address_line1: form.address_line1 || undefined,
        suburb: form.suburb || undefined,
        state: form.state || undefined,
        postcode: form.postcode || undefined,
        description: form.description || undefined,
      };
      const { data } = await projectAPI.create(payload);
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  }

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px',
    border: '1px solid var(--hairline)', borderRadius: 4,
    background: 'var(--card)', fontSize: 13,
  };
  const lbl = (text) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>
      {text}
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>New project</div>
            <h2 className="serif" style={{ fontSize: 20, margin: '2px 0 0', color: 'var(--forest)', fontWeight: 600 }}>Create development project</h2>
          </div>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13 }}>
                {error}
              </div>
            )}

            <div className="field">
              {lbl('Project name *')}
              <input style={fieldStyle} value={form.name} onChange={set('name')} placeholder="e.g. Bondi Townhouse Development" required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                {lbl('Project type')}
                <select style={fieldStyle} value={form.project_type} onChange={set('project_type')}>
                  {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t || '— select —'}</option>)}
                </select>
              </div>
              <div className="field">
                {lbl('Budget ($)')}
                <input style={fieldStyle} type="number" value={form.budget} onChange={set('budget')} placeholder="e.g. 850000" />
              </div>
            </div>

            <div className="field">
              {lbl('Address')}
              <AddressAutocomplete
                value={form.address_line1}
                onChange={(v) => setForm((f) => ({ ...f, address_line1: v }))}
                onSelect={(parts) => setForm((f) => ({ ...f, ...parts }))}
                fieldStyle={fieldStyle}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div className="field">
                {lbl('Suburb')}
                <input style={fieldStyle} value={form.suburb} onChange={set('suburb')} placeholder="Auto-filled or type" />
              </div>
              <div className="field">
                {lbl('State')}
                <input style={fieldStyle} value={form.state} onChange={set('state')} placeholder="NSW" />
              </div>
              <div className="field">
                {lbl('Postcode')}
                <input style={fieldStyle} value={form.postcode} onChange={set('postcode')} placeholder="2000" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                {lbl('Start date')}
                <input style={fieldStyle} type="date" value={form.start_date} onChange={set('start_date')} />
              </div>
              <div className="field">
                {lbl('Target completion')}
                <input style={fieldStyle} type="date" value={form.target_completion} onChange={set('target_completion')} />
              </div>
            </div>

            <div className="field">
              {lbl('Description')}
              <textarea
                value={form.description}
                onChange={set('description')}
                placeholder="Brief overview of the project…"
                style={{ ...fieldStyle, height: 72, padding: '8px 10px', resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
